/**
 * Chi tiết một tủ: trạng thái, vị trí công tắc vật lý, 3 nút gạt điều khiển ngõ ra,
 * trả quyền về tự động, thông số điện mới nhất + biểu đồ 24 giờ, nhật ký lệnh, cảnh báo.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Activity, AlertTriangle, CalendarClock, Check, ChevronLeft, Clock, Copy, Gauge, Hand,
  MapPin, RefreshCw, RotateCcw, Settings, Terminal, Wifi, WifiOff,
} from 'lucide-react';
import {
  Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { api, errorMessage, ApiError } from '../lib/api';
import {
  qk, useAlarms, useCabinet, useCommands, useDeviceLogs, useMeterHistory, useNguonGiuSong, useSoDoTrucTiep,
} from '../lib/queries';
import { useAuth, useQuyen } from '../lib/auth';
import { BangKhoaTuyen, DauDangKiem, KhoaTheoTick, KhoaTheoTuyen, HuyHieuKhoa } from '../components/Khoa';
import { type QuyetDinhCoTuyen } from '../lib/quyen';
import {
  Badge, Button, Card, EmptyState, ErrorBox, Modal, Spinner, Switch,
  WarningBanner, useToast, Metric,
} from '../components/ui';
import {
  fmtAgo, fmtDate, fmtDateTime, fmtDateTimeSec, fmtNum, fmtTime, minToStr, nhanCanhBao } from '../lib/format';
import {
  BE_RONG_TRUC_Y, CAU_TRONG, DAI_CUA, TRUC_CUA, chuSoTruc, diemTheoDonVi, duLieuBieuDo, mienTruc, type DuongDo,
} from '../lib/bieudo';
import { hienCongSuat } from '../lib/congsuat';

/** TK-20: tên + màu từng đường. Công suất KHÔNG gắn đơn vị: KWS báo W, MFM383A báo kW — máy chủ chưa biết công tơ nào. */
const NHAN_DUONG: Record<DuongDo, { ten: string; mau: string }> = {
  v: { ten: 'Điện áp pha 1 (V)', mau: 'rgb(var(--tot))' },
  p: { ten: 'Công suất pha 1', mau: 'rgb(var(--nhan))' },   // đơn vị ghép lúc vẽ từ `donViCongSuat` (TK-19)
  i: { ten: 'Dòng pha 1 (A)', mau: 'rgb(var(--canh))' },
};
import {
  COMMAND_STATE_LABEL, MODE_LABEL, STATUS_LABEL, SWITCH_LABEL,
  type CabinetOutput, type CommandState,
} from '../lib/types';
import CaiDatThietBi, {
  useCaiDatTu, tomTatELR, tomTatLopBaoVe, type DongCanChuY,
} from '../components/CaiDatThietBi';
import { useConTrongHan } from '../lib/useHanSuDung';
import { CAU_HET_HAN } from '../lib/hansudung';
import { moTaTaiLenh } from '../lib/lenhmota';
import {
  cheMaThietBi, dongNhatKy, ketQuaLenh, noiDungLenh, type LoiNguoi,
} from '../lib/loi_nguoi';
import { ChiTietKyThuat, useLaQuanTri } from '../components/ChiTietKyThuat';
import { docNguonGiu, nhanGiuNgo, cauTraQuyen } from '../lib/nguongiu';

/**
 * Số đã định dạng, hoặc `null` khi KHÔNG CÓ SỐ LIỆU.
 *
 * ⚠ Khác `fmtNum()`: `fmtNum` trả về chuỗi `'—'` cho giá trị rỗng, và một chuỗi thì `Metric`
 * không phân biệt được với số liệu thật ⇒ nó sẽ vẽ `— V` như thể có đo. Ở đây trả `null` để
 * `Metric` vẽ đúng mức "chưa có số liệu".
 */
function soHoac(v: number | null | undefined, chuSo: number): string | null {
  return v === null || v === undefined || !Number.isFinite(v) ? null : fmtNum(v, chuSo);
}

export default function CabinetDetailPage() {
  const { id = '' } = useParams();
  /*
   * 🚨 `nguonGiuQuyen` KHÔNG đi qua CSDL — nó chỉ tới bằng khung `cabinet.state`. Móc này là
   *   đường duy nhất nó vào được trang, và nó buộc vẽ lại theo SỐ PHIÊN BẢN của kho sống,
   *   nên nhãn đổi được cả khi `on` KHÔNG đổi (ca: màn hình chiếm quyền, rơ-le giữ nguyên).
   */
  const nguonGiuSong = useNguonGiuSong(id);
  const qc = useQueryClient();
  const toast = useToast();
  /*
   * ➕ `P-6` `B4` mục 3 — bật/tắt đi bằng CỜ `canControl` CỦA TUYẾN chứa tủ này, không bằng
   *   hạng. Hook lấy ở đây (trước mọi `return` sớm); tuyến thì chỉ biết sau khi có dữ liệu tủ,
   *   nên câu hỏi thật đặt ở dưới.
   */
  const { coCoTuyen } = useAuth();
  /* Ghi cấu hình tủ = tick `doi_nguong` — khớp đúng cổng `/api/lenh/dat` ở máy chủ. */
  const coTheGhiCaiDat = useQuyen('doi_nguong');
  const [hours, setHours] = useState(24);

  const cabinet = useCabinet(id);
  const meter = useMeterHistory(id, hours);
  const soDoTrucTiep = useSoDoTrucTiep(id);   // TK-16 B4: rót từ khung `cabinet.meter`, không đọc CSDL
  const commands = useCommands(id, 40);
  const alarms = useAlarms(id);
  const logs = useDeviceLogs(id, 60);

  useEffect(() => {
    document.title = cabinet.data ? `${cabinet.data.name} — SDKD` : 'Chi tiết tủ — SDKD';
  }, [cabinet.data]);

  const refreshCabinet = () => {
    qc.invalidateQueries({ queryKey: qk.cabinet(id) });
    qc.invalidateQueries({ queryKey: qk.commands(id) });
    qc.invalidateQueries({ queryKey: qk.tree });
  };

  /** Bật/tắt MỘT ngõ ra */
  const control = useMutation({
    mutationFn: (vars: { index: number; on: boolean }) =>
      api.post<{ commandId: string; state: CommandState; ackMode: string }>(
        `/api/cabinets/${id}/control`,
        { outputs: [{ index: vars.index, on: vars.on }] }
      ),
    onSuccess: (res, vars) => {
      toast.success(
        `Đã gửi lệnh ${vars.on ? 'BẬT' : 'TẮT'} ngõ ra ${vars.index}`,
        res.ackMode === 'INFERRED'
          ? 'Tủ chạy giao thức v1 nên không có xác nhận tường minh — trạng thái sẽ cập nhật khi tủ báo về.'
          : 'Đang chờ tủ xác nhận.'
      );
      refreshCabinet();
    },
    onError: (e) => {
      const hint = e instanceof ApiError ? e.hint : null;
      toast.error(errorMessage(e), hint ?? undefined);
    },
  });

  /** Trả quyền điều khiển về cho lịch tự động của tủ */
  const release = useMutation({
    mutationFn: () => api.post<{ commandId: string; state: CommandState }>(`/api/cabinets/${id}/release`),
    onSuccess: () => {
      /*
       * ⛔ VẪN LÀ "ĐÃ GỬI", KHÔNG PHẢI "ĐÃ TRẢ QUYỀN" — và lý do nay KHÁC lý do cũ, nên chép
       *   lại cho đúng (L21). Lý do cũ: *"v1 không có đường ACK"*. Từ 07/09/2026 tủ v2 đi bằng
       *   `Command{ kind = CMD_RELEASE_CONTROL }` mang `commandId` nên **CÓ ACK thật**.
       *
       * 🔴 Nhưng ACK trả lời câu *"tủ đã NHẬN lệnh"*, không trả lời *"tủ đã TRẢ QUYỀN"*. Hai
       *   câu ấy chỉ giống nhau với người đã biết trước câu trả lời. Nhân chứng cho câu thứ
       *   hai là cột **Giữ tay** (`manualHold`) rụng hết về `false` ở khung tủ báo kế tiếp —
       *   và cột ấy nay CÓ NGUỒN GHI, dev_device đã đo trên phần cứng (`giu tay 0x1→0x3→0x7`,
       *   NV93 §3.3). Nên câu hướng dẫn chỉ thẳng vào nó, thay vì bảo người trực đoán qua
       *   trạng thái BẬT/TẮT.
       */
      toast.success('Đã gửi lệnh trả quyền về chế độ tự động',
        'Xong khi ba ngõ ra hết nhãn “Đang giữ” ở lần tủ báo về kế tiếp. Còn nhãn ⇒ tủ chưa trả quyền, dù lệnh đã được nhận. Nếu nhãn ghi một nguồn KHÁC ứng dụng, trả quyền phải thực hiện tại chính nguồn đó.');
      refreshCabinet();
    },
    onError: (e) => toast.error('Không trả quyền được', errorMessage(e)),
  });

  /** Đồng bộ đồng hồ thời gian thực của tủ */
  const syncRtc = useMutation({
    mutationFn: () => api.post<{ ok: boolean; coAck: boolean; ketQua: string | null; chiTiet: string | null }>(
      `/api/cabinets/${id}/sync-rtc`),
    /*
     * ⛔ BA KẾT QUẢ, KHÔNG PHẢI MỘT (YC-39). Máy chủ nay đi qua `datGio()` — nó CHỜ ACK và
     *   phân biệt: tủ nhận · tủ TỪ CHỐI · KHÔNG có ACK. Câu cũ ("đã gửi lệnh") đúng cho cả ba,
     *   nên nó không nói gì cả — và nó từng che đúng ca khung không ai đọc.
     * ⚠ "Không có ACK" KHÔNG được trộn với "ACK nói OK" (chốt YC-16 vòng 2).
     */
    onSuccess: (r) => {
      if (r.ok) toast.success('Tủ đã nhận giờ mới', 'Đồng hồ tủ đã được đặt theo giờ máy chủ.');
      else if (r.coAck) toast.error('Tủ TỪ CHỐI giờ gửi xuống', r.chiTiet ?? r.ketQua ?? undefined);
      else toast.error('Đã gửi, nhưng tủ KHÔNG xác nhận',
        'Không kết luận được là đồng hồ đã đặt xong — thử lại, hoặc đọc trạng thái tủ để đối chiếu.');
    },
    onError: (e) => toast.error('Không đồng bộ được đồng hồ', errorMessage(e)),
  });

  /* TK-20: điểm vẽ từ các Ô gom (`/meter/o`), giữ nguyên `null` — xem lib/bieudo.ts. */
  /* TK-22 C: đường công suất quy về W theo công tơ rồi chọn W/kW theo độ lớn CẢ cửa sổ (không gắn cứng theo model). */
  const donViCongTo = cabinet.data?.donViCongSuat ?? null;
  const { diem: diemDo, donViP } = useMemo(
    () => diemTheoDonVi(meter.data ? duLieuBieuDo(meter.data) : [], donViCongTo), [meter.data, donViCongTo]);
  const chuSo = useMemo((): Record<DuongDo, number> => ({
    v: 0, p: chuSoTruc(mienTruc(diemDo, TRUC_CUA.p)), i: chuSoTruc(mienTruc(diemDo, TRUC_CUA.i)),
  }), [diemDo]);

  /*
   * ⚠ ĐẶT TRƯỚC MỌI `return` SỚM — đây là một hook. Bản đầu tôi viết nó xuống dưới, cạnh chỗ
   *   dùng, và đó là một hook gọi CÓ ĐIỀU KIỆN: lần tải đầu (`isLoading`) không gọi, lần sau
   *   có gọi ⇒ React lệch thứ tự hook. Kiểu lỗi này không đỏ ở `tsc`, nó nổ lúc chạy.
   *
   * 🔴 Và đây là chỗ dev_device hỏi trúng (YC-35): máy chủ chấm TẠI MỘT THỜI ĐIỂM, còn kết
   *   luận ấy nằm lại trong bộ nhớ đệm trình duyệt. `useCabinet` không hỏi lại theo nhịp, và
   *   tủ chết thì KHÔNG có sự kiện nào đẩy xuống — nên một câu "còn dùng được" sẽ đứng nguyên
   *   trên màn hình. Đông lạnh chui lên một tầng cao hơn. ⇒ Đếm giờ cho chính kết luận ấy.
   */
  const conHanSoDo = useConTrongHan(cabinet.data?.soDo?.hanConLaiMs, cabinet.data?.soDo);

  /*
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * ➕ `GD-03` — BA LỆNH ĐỌC CỦA TỦ NAY GỌI **Ở ĐÂY**, một lần, rồi chia cho ba chỗ vẽ:
   *   ô số "Đồng hồ tủ" (hàng 2) · thẻ "Cần chú ý" (hàng 3) · khối "Cài đặt tủ" (hàng 5).
   *
   * ⛔ ĐẶT TRƯỚC MỌI `return` SỚM — nó là một hook, và ba dòng `isLoading/isError/!data` ngay
   *   dưới đây là ba lối ra. Gọi nó sau đó là gọi hook CÓ ĐIỀU KIỆN: lần tải đầu không gọi,
   *   lần sau có gọi ⇒ React lệch thứ tự hook. Cùng cái bẫy `useConTrongHan` ở ngay trên, và
   *   kiểu lỗi này **không đỏ ở `tsc`** — nó nổ lúc chạy.
   * ⚠ `deviceId` chưa có ⇒ truyền `null`, hook tự không gọi gì. Không cần cổng ở đây.
   * ═══════════════════════════════════════════════════════════════════════════════════════
   */
  const ct = useCaiDatTu(cabinet.data?.deviceId ?? null, cabinet.data?.status === 'ONLINE');

  /*
   * ⛔ KHỐI "CÀI ĐẶT TỦ" MẶC ĐỊNH **THU** — nguyên tắc lee chốt qua mẫu: *thứ cài một lần thì
   *   xuống dưới và thu lại.* Nhớ lựa chọn theo TỪNG TỦ (`sessionStorage`), không nhớ chung:
   *   mở cài đặt ở tủ A không có nghĩa là muốn mở ở tủ B.
   * ⚠ `sessionStorage` chứ không `localStorage`: đây là một trạng thái của PHIÊN LÀM VIỆC
   *   ("tôi đang chỉnh cái tủ này"), không phải một tuỳ chọn diện mạo. Nhớ nó qua nhiều ngày
   *   là mở sẵn một khối cấu hình cho một người chỉ muốn xem trạng thái.
   * 🔴 Và `try/catch`: cửa sổ riêng tư / trình duyệt chặn lưu trữ thì nó NÉM — một tuỳ chọn
   *   hiển thị không được phép làm sập cả trang (cùng luật với `KHOA_CAY`, `kapp.giaodien`).
   */
  const KHOA_MO_CAI_DAT = `sdkd.caiDat.${id}`;
  const [moCaiDat, setMoCaiDat] = useState(() => {
    try { return sessionStorage.getItem(KHOA_MO_CAI_DAT) === '1'; } catch { return false; }
  });
  const doiMoCaiDat = (v: boolean) => {
    setMoCaiDat(v);
    try { sessionStorage.setItem(KHOA_MO_CAI_DAT, v ? '1' : '0'); } catch { /* riêng tư — bỏ qua */ }
  };
  /** Nút "Cài đặt tủ" ở đầu trang: MỞ khối rồi cuộn tới nó — mở mà không cuộn thì không ai thấy. */
  const moVaCuonToiCaiDat = () => {
    doiMoCaiDat(true);
    setTimeout(() => {
      document.getElementById('khoi-cai-dat')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 0);
  };

  /** Hộp "xem tất cả" nhật ký lệnh — bảng ở trang chỉ 10 dòng gần nhất. */
  const [xemHetLenh, setXemHetLenh] = useState(false);

  if (cabinet.isLoading) return <Spinner label="Đang tải thông tin tủ…" />;
  if (cabinet.isError) return <ErrorBox error={cabinet.error} onRetry={() => void cabinet.refetch()} />;
  if (!cabinet.data) return null;

  const c = cabinet.data;
  const online = c.online;
  /*
   * 🔴 BỐN CA, KHÔNG PHẢI HAI — và câu phụ đề phải nói đúng ca người đọc đang ở:
   *   tủ chưa gán tuyến · chưa được gán tuyến ấy · có tuyến nhưng không có cờ · được phép.
   * Gộp thành "chỉ được xem" là bắt người ta đoán mình thuộc ca nào, và ba trong bốn ca có
   * đường đi tiếp khác nhau.
   */
  const qdBatTat = coCoTuyen('canControl', c.route?.id ?? null);
  const canControl = qdBatTat === 'cho-qua';
  const switchPos = c.switchPos ?? null;
  const lastMeter = c.lastMeter;
  /* TK-22 C: bỏ "kW" gắn cứng — KWS gửi W. Quy theo công tơ, W/kW theo độ lớn; công tơ chưa rõ ⇒ không đơn vị. */
  const csHien = lastMeter && lastMeter.power != null && Number.isFinite(lastMeter.power)
    ? hienCongSuat(lastMeter.power, c.donViCongSuat) : null;
  /*
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * YC-34 lớp 1 — CÓ MẪU ĐO ≠ MẪU ĐO CÒN DÙNG ĐƯỢC.
   *
   * ⛔ `c.soDo?.ve === true` chứ KHÔNG phải `c.soDo?.ve` hay `!== false`: `soDo` vắng (máy
   *   chủ đời cũ) phải rơi về "chưa biết ⇒ không vẽ", không rơi về "vẽ". Một chỗ duy nhất
   *   trong tệp này được quyền quyết định vẽ hay không là dòng dưới đây.
   * ═══════════════════════════════════════════════════════════════════════════════════════
   */
  const veDuocSoDo = c.soDo?.ve === true && conHanSoDo;
  /*
   * ⚠ Câu này hỏi `'cau' in c.soDo` chứ KHÔNG hỏi lại `.ve`: chỉ được có ĐÚNG MỘT chỗ đọc
   *   `soDo.ve` trong tệp (dòng trên). Hai chỗ là hai chỗ sẽ trôi khỏi nhau, và chỗ trôi sẽ
   *   là chỗ vẽ ra số cũ. Phép `[64]` đếm đúng chuyện này.
   */
  const cauKhongVe =
    c.soDo === undefined
      ? 'Chưa rõ số còn mới hay không — không vẽ.'   // TK-24: giữ nghĩa (Luật số 0), bỏ lời tự biện
      : 'cau' in c.soDo
        ? c.soDo.cau
        /* Còn `ve: true` mà tới đây thì nghĩa là nó ĐÃ HẾT HẠN ngay trên màn hình. */
        : CAU_HET_HAN;

  return (
    <div className="space-y-4">
      {/*
        * ══════════════════════════════════════════════════════════════════════════════════
        * ➕ `GD-03` 11/09/2026 — TRANG XẾP LẠI KIỂU **BẢNG ĐIỀU KHIỂN** (mẫu `_BUS/23`).
        *
        * 🔴 NGUYÊN TẮC lee CHỐT QUA MẪU, một câu: *thứ nhìn mỗi ngày lên trên và to; thứ cài
        *   một lần xuống dưới và thu lại.* Bản cũ xếp theo thứ tự **chúng tôi làm ra chúng**
        *   (thông tin → điều khiển → thông số → nhật ký → cài đặt → sức khoẻ → cảnh báo), nên
        *   một người trực muốn biết *"tủ này có điện không, đèn có sáng không"* phải cuộn qua
        *   một bảng 11 dòng siêu dữ liệu trước.
        *
        * ⛔ ĐỔI CHỖ, KHÔNG ĐỔI LUẬT: mọi hook, mọi mutation, mọi cổng quyền (`canControl` theo
        *   tuyến · `doi_nguong`), mọi câu chữ protectEn/ELR và mọi mức "chưa có số liệu" giữ
        *   NGUYÊN VĂN. Thứ duy nhất đổi cấu trúc là ba lệnh đọc tủ gom vào `useCaiDatTu()` —
        *   và gom lại là để **bớt** một nguồn sự thật, không phải thêm.
        * ══════════════════════════════════════════════════════════════════════════════════
        */}

      {/* ══════════ HÀNG 1 · ĐẦU TRANG ══════════ */}
      <div className="flex flex-wrap items-center gap-2">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-chu-2 hover:text-chu-1">
          <ChevronLeft className="h-4 w-4" /> Tổng quan
        </Link>
        <span className="text-chu-3">›</span>
        <Link to="/con-duong" className="text-sm text-chu-2 hover:text-chu-1">Tuyến</Link>
        <span className="text-chu-3">›</span>
        <span className="text-sm text-chu-2">{c.route?.tenDayDu ?? 'Chưa gán tuyến chiếu sáng'}</span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/*
          * ⚠ TÊN TỦ dùng chữ TIÊU ĐỀ (Playfair), mã tủ dùng chữ thường và nhỏ — mẫu `23`.
          *   Mã `AUTO-001 · GW-…` là thứ người ta ĐỐI CHIẾU, không phải thứ người ta đọc.
          */}
        <h1 className="chu-tieude m-0 text-[22px] font-semibold text-chu-1">
          {c.name}
          <span className="ml-2 font-sans text-[13px] font-normal text-chu-2">
            {c.code ?? 'chưa có mã'} · {c.hardwareId}
          </span>
        </h1>

        {/*
          * ➕ HUY HIỆU ĐẦU TRANG — và chúng **thay cho** bốn dòng của bảng "Thông tin tủ" cũ
          *   (trạng thái · công tắc vật lý · giao thức · firmware). Bốn thứ ấy là thứ người
          *   trực liếc một cái là xong; bắt chúng nằm trong một bảng nhãn-giá-trị 11 dòng là
          *   bắt mắt dò dọc để lấy một tin có thể đọc bằng một cái liếc ngang.
          * ⛔ KHÔNG LẶP: đã lên huy hiệu thì lưới "Thông tin tủ" ở hàng 3 KHÔNG in lại chúng.
          */}
        <Badge tone={online ? 'green' : 'red'}>
          {online ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
          {online ? 'Đang kết nối' : STATUS_LABEL[c.status]}
        </Badge>
        {/* ⛔ TẮT dùng tone 'red', KHÔNG 'slate' — `slate` là màu của "không rõ". */}
        {switchPos ? (
          <Badge tone={switchPos === 'AUTO' ? 'green' : switchPos === 'MANUAL' ? 'amber' : 'red'}>
            <Hand className="h-3 w-3" /> {SWITCH_LABEL[switchPos]}
          </Badge>
        ) : <Badge tone="slate">công tắc chưa rõ</Badge>}
        <Badge tone={c.protocolVer >= 2 ? 'slate' : 'amber'}>
          v{c.protocolVer}{c.firmwareVersion ? ` · fw ${c.firmwareVersion}` : ' · fw chưa rõ'}
        </Badge>
        {/* TK-19: bản firmware THẬT (git) — chỉ REGISTER ghi được, khung sau không đè. */}
        <Badge tone={c.fwDirty ? 'red' : 'slate'}>
          {c.fwGit ? `bản ${c.fwGit}${c.fwDirty ? ' · dirty' : ''}` : 'bản chưa khai'}
        </Badge>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button size="sm" icon={<RefreshCw className="h-4 w-4" />} onClick={refreshCabinet} loading={cabinet.isFetching}>
            Làm mới
          </Button>
          <Link to={`/lich/${c.id}`}>
            <Button size="sm" variant="secondary" icon={<CalendarClock className="h-4 w-4" />}>Lịch đóng cắt</Button>
          </Link>
          {/*
            * ➕ `GD-09b` — BA CA Ở TẦNG TICK. Ảnh tự chụp bắt được đúng cái nút này đeo huy hiệu
            *   hổ phách *"Không có quyền: Đổi ngưỡng bảo vệ, cài đặt tủ"* trong lúc `/me` chưa
            *   trả lời — một LỜI KHAI dựng từ `user?.quyen ?? []`. `KhoaTheoTick` tự hỏi
            *   `daBietQuyen` nên trang này không còn cầm `boolean` để rẽ nhầm.
            */}
          <KhoaTheoTick khoa="doi_nguong">
            <Button
              size="sm"
              variant="secondary"
              icon={<Clock className="h-4 w-4" />}
              loading={syncRtc.isPending}
              disabled={!online || !coTheGhiCaiDat}
              onClick={() => syncRtc.mutate()}
            >
              Đồng bộ giờ
            </Button>
          </KhoaTheoTick>
          {/*
            * ⚠ NÚT CHÍNH DUY NHẤT CỦA ĐẦU TRANG. Nó KHÔNG bị khoá theo `doi_nguong`: mở một
            *   khối để ĐỌC cấu hình là việc ai cũng làm được; cái bị khoá là ô nhập và nút
            *   Gửi bên trong (`coTheGhi` truyền xuống, y như cũ). Khoá cả cửa vào là giấu mất
            *   thông tin khỏi người chỉ có quyền xem — đúng thứ luật "khoá, không ẩn" cấm.
            */}
          <Button size="sm" icon={<Settings className="h-4 w-4" />} onClick={moVaCuonToiCaiDat}>
            Cài đặt tủ
          </Button>
        </div>
      </div>

      {/* Cảnh báo bối cảnh vận hành */}
      {!online && (
        <WarningBanner tone="red" title="Tủ đang MẤT KẾT NỐI">
          <p className="mt-1 text-xs">
            Thấy lần cuối: {fmtDateTime(c.lastSeenAt)} ({fmtAgo(c.lastSeenAt)}). Lệnh bật/tắt gửi lúc này
            sẽ bị từ chối; hãy kiểm tra nguồn, SIM/đường truyền của tủ.
          </p>
        </WarningBanner>
      )}

      {/*
        🔴 TẮT KHÔNG PHẢI "CHƯA BIẾT" — nó là *"người ta đã tắt cái tủ này bằng tay"*, và đó
        là thông tin quan trọng nhất trong ba trạng thái công tắc. Nên nó phải ĐỎ, không phải
        vàng như BẰNG TAY: BẰNG TAY nghĩa là lịch có thể không chạy; TẮT nghĩa là **đèn không
        sáng, bất kể ta gửi gì xuống**.
      */}
      {online && switchPos && switchPos !== 'AUTO' && (
        <WarningBanner
          tone={switchPos === 'OFF' ? 'red' : 'amber'}
          title={`Công tắc vật lý tại tủ đang ở vị trí ${SWITCH_LABEL[switchPos]}`}
        >
          <p className="mt-1 text-xs">
            {switchPos === 'OFF'
              ? 'Ở vị trí TẮT, đèn không sáng dù lệnh từ xa có được nhận. Cần gạt công tắc về AUTO tại hiện trường.'
              : 'Ở vị trí BẰNG TAY, tủ ưu tiên thao tác tại chỗ; lịch tự động có thể không chạy.'}
          </p>
        </WarningBanner>
      )}

      {/* ══════════ HÀNG 2 · BỐN Ô SỐ ══════════ */}
      {/*
        * ═══════════════════════════════════════════════════════════════════════════════════
        * 🔴 BỐN Ô NÀY LÀ CÂU TRẢ LỜI CHO BỐN CÂU HỎI NGƯỜI TRỰC HỎI MỖI NGÀY:
        *     có điện không · đang ăn bao nhiêu · đèn có sáng không · tủ có biết mấy giờ không
        *   Mọi thứ khác trên trang này là thứ người ta tra khi đã có bốn câu trả lời ấy.
        *
        * ⛔ VÀ KHÔNG Ô NÀO ĐƯỢC VẼ MỘT SỐ CŨ. `veDuocSoDo` là cái cửa DUY NHẤT quyết định vẽ
        *   hay không (đã có từ YC-34 lớp 1) — ba ô đầu đi qua đúng cửa ấy, không ô nào tự mở
        *   một cửa riêng. Ô thứ tư (đồng hồ) có cửa riêng vì nguồn khác: nó đến từ một lệnh
        *   hỏi thẳng tủ, không từ mẫu đo.
        * ═══════════════════════════════════════════════════════════════════════════════════
        */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {/* ① ĐIỆN ÁP — 1 hay 3 số, theo SỐ PHA CỦA TỦ */}
        <Card
          /* GD-13 — câu "Pha dưới 50 V coi là không có điện." dời vào ⓘ cạnh tiêu đề (đường c). */
          title="Điện áp"
          goiY={{ nhan: 'Ngưỡng có điện', noiDung: 'Pha dưới 50 V coi là không có điện.' }}
          actions={
            /*
             * ⛔ ĐỌC `tuoiMs`, **KHÔNG** ĐỌC `.ve` LẦN THỨ HAI. Bản đầu tôi viết
             *   `veDuocSoDo && c.soDo?.ve ? …` — và phép `[64]` đỏ ngay, đúng luật nó sinh ra
             *   để giữ: *chỉ được có ĐÚNG MỘT chỗ đọc `soDo.ve` trong tệp này.* Hai chỗ là
             *   hai chỗ sẽ trôi khỏi nhau, và chỗ trôi sẽ là chỗ vẽ ra số cũ.
             * ⚠ `'tuoiMs' in c.soDo` hợp lệ cho CẢ HAI nhánh của kiểu hợp (`ve:true` có
             *   `tuoiMs: number`, `ve:false` có `tuoiMs: number | null`) — nên nó lấy được
             *   tuổi mà không phải hỏi lại câu đã có người trả lời.
             */
            <span className="text-xs text-chu-3">
              {veDuocSoDo && c.soDo && 'tuoiMs' in c.soDo ? `tủ đọc ${moTaTuoi(c.soDo.tuoiMs)}` : ''}
            </span>
          }
        >
          {veDuocSoDo && lastMeter ? (
            <>
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {/*
                  * 🔴 SỐ CỘT THEO **SỐ PHA CỦA TỦ**, không theo "có mấy số về" — nguyên văn
                  *   luật cũ, chép lên đây cùng với ô. `soPha === null` = CHƯA BIẾT ⇒ vẽ đủ
                  *   ba: pha vừa MẤT cũng không có số liệu (proto3 bỏ 0 khỏi dây), nên ẩn
                  *   theo "không có số" là đúng lúc mất pha thì cột ấy biến mất thay vì đỏ.
                  */}
                {[
                  { pha: 1, nhan: 'Pha A', v: lastMeter.v1 },
                  { pha: 2, nhan: 'Pha B', v: lastMeter.v2 },
                  { pha: 3, nhan: 'Pha C', v: lastMeter.v3 },
                ]
                  .filter((x) => c.soPha == null || x.pha <= c.soPha)
                  .map((x) => (
                    <div key={x.pha} className="min-w-0">
                      <Metric giaTri={soHoac(x.v, 1)} manh coNho />
                      <p className="text-xs text-chu-3">
                        {c.soPha === 1 ? 'Điện áp' : x.nhan} · V
                      </p>
                    </div>
                  ))}
              </div>
            </>
          ) : (
            <ChuaCoSo cau={cauKhongVe} />
          )}
        </Card>

        {/* ② CÔNG SUẤT */}
        <Card title="Công suất">
          {veDuocSoDo && lastMeter ? (
            <>
              <Metric giaTri={csHien ? fmtNum(csHien.tri, csHien.chuSo) : null} donVi={csHien?.donVi ?? undefined} manh coNho />
              {/*
                * ⚠ BA SỐ PHỤ NỐI BẰNG `·`, và mỗi số TỰ VẮNG MẶT khi không có — không in
                *   "hệ số CS —". Một dấu gạch nằm cạnh hai số thật trông như một phép đo đã
                *   về và bằng không.
                */}
              <p className="mt-1.5 text-xs text-chu-3">
                {[
                  soHoac(lastMeter.a1, 2) && `hệ số CS ${soHoac(lastMeter.a1, 2)}`,
                  soHoac(lastMeter.i1, 1) && `dòng ${soHoac(lastMeter.i1, 1)} A`,
                  soHoac(lastMeter.total, 1) && `điện năng ${soHoac(lastMeter.total, 1)} kWh`,
                ].filter(Boolean).join(' · ') || 'chưa có số phụ nào'}
              </p>
            </>
          ) : (
            <ChuaCoSo cau={cauKhongVe} />
          )}
        </Card>

        {/* TK-22 B — NGƯỠNG CẢNH BÁO QUÁ DÒNG của tủ này: máy chủ tự xét, CHỈ BÁO, không cắt. */}
        <Card title="Cảnh báo quá dòng">
          <TheNguongQuaDong cabinetId={c.id} nguongA={c.nguongCanhBaoQuaDongA ?? null}
                            dongA={veDuocSoDo && lastMeter ? lastMeter.i1 ?? null : null} />
        </Card>

        {/* TK-16 B4 — SỐ ĐO TRỰC TIẾP: đọc từ khung `cabinet.meter` (không qua CSDL). Firmware mới gửi
            30 s/lần hoặc NGAY khi biến động lớn; khung sự kiện được đánh dấu. */}
        <Card title="Số đo trực tiếp">
          <BangSoDoTrucTiep sd={soDoTrucTiep.data ?? null} soPha={c.soPha ?? null} donViCongTo={c.donViCongSuat ?? null} />
        </Card>

        {/* ③ NGÕ RA */}
        <Card title="Ngõ ra">
          {/*
            * ⛔ "TỦ TỰ KHAI" phải đi cùng con số này, mọi lúc — `outputs[].on` dựng từ
            *   `cache_wb.output_state[]`, mảng mà đường điều khiển ghi vào khi RA LỆNH, không
            *   phải số đọc từ tiếp điểm ([G1]). Bỏ ngoặc ấy là hứa một phép đo không tồn tại.
            */}
          {/*
            * 🔴 09/10/2026 — lee khoanh đỏ cả dòng này. Ba mẩu chữ gộp lại thành một câu không
            *   ai đọc: "Tủ tự khai · có ngõ ĐANG BỊ GIỮ · đổi lần cuối 24 giây trước".
            *
            * ⛔ "TỦ TỰ KHAI" KHÔNG BỊ BỎ, nó chuyển vào `title` của chính con số — luật cũ ghi
            *   ngay trên đây vẫn đúng (`outputs[].on` dựng từ `cache_wb.output_state[]`, không
            *   phải số đọc từ tiếp điểm), nên con số không được phép đứng một mình mà KHÔNG có
            *   đường tra lại. Nhưng một lời chú thích luôn hiện là một lời không ai còn đọc.
            * ⚠ Dòng dưới chỉ còn hiện KHI CÓ ngõ bị giữ — tức khi nó thật sự là một việc cần làm.
            *   "Đổi lần cuối" bỏ hẳn: giờ ấy đã có trên từng thẻ ngõ ra.
            */}
          <span title="Tủ tự khai — số này dựng từ lệnh đã gửi, không phải số đọc từ tiếp điểm">
            <Metric giaTri={c.outputs.filter((o) => o.isOn).length} donVi={`/ ${c.outputs.length} đang bật`} manh coNho />
          </span>
          {c.outputs.some((o) => nhanGiuNgo(o.nguonGiuQuyen, o.manualHold).kieu !== 'trong') && (
            <p className="mt-1.5 text-xs text-canh">
              {c.outputs.filter((o) => nhanGiuNgo(o.nguonGiuQuyen, o.manualHold).kieu !== 'trong').length} ngõ đang bị giữ
            </p>
          )}
        </Card>

        {/* ④ ĐỒNG HỒ TỦ */}
        <Card
          title="Đồng hồ tủ"
          actions={
            <span className="text-xs text-chu-3">
              lệch RTC {c.rtcDriftSec != null ? `${c.rtcDriftSec} s` : '—'}
            </span>
          }
        >
          <DongHoTu gio={ct.gio} c={c} />
        </Card>
      </div>

      {/* ══════════ HÀNG 3 · ĐIỀU KHIỂN + BIỂU ĐỒ │ CẦN CHÚ Ý + THÔNG TIN TỦ ══════════ */}
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1.25fr_1fr]">
        {/* ── trái: điều khiển ngõ ra + biểu đồ, CÙNG MỘT THẺ ── */}
        <Card
          /* GD-13 — cả phụ đề ("Lệnh gửi thẳng xuống tủ · … TỦ TỰ KHAI …") dời vào ⓘ (đường c). */
          title="Điều khiển ngõ ra"
          goiY={{ nhan: 'Điều khiển ngõ ra', noiDung: 'Lệnh gửi thẳng xuống tủ. Trạng thái ngõ ra dưới đây là tủ tự khai, chưa phải số đọc từ tiếp điểm.' }}
          /*
           * ⚠ *"tủ tự khai"* chứ không phải *"tủ báo về"*: xem chú thích ở ô ③.
           * ⚠ Phụ đề KHÔNG tự ghép câu "chỉ xem …": câu ấy nằm ở **một băng** đầu khối
           *   (`BangKhoaTuyen`), đúng ý lee — một băng gọn thay cho câu lặp ở từng ngõ ra.
           */
          actions={
            /*
             * KHOÁ, không ẩn: người trực phải thấy chức năng ấy TỒN TẠI để còn đi xin quyền.
             * ➕ `GD-09b` — và BA CA chứ không hai: bản cũ rẽ theo `canControl` (`boolean`), nên
             *   lúc `/me` chưa trả lời nút này đã kịp đeo khung hổ phách *"Không có quyền:
             *   Bật/tắt đèn"*. `KhoaTheoTuyen` nhận `qdBatTat` nên ca `'chua-biet'` ra khung
             *   xám. Xem khối ở `KhoaTheoTuyen` (`Khoa.tsx`).
             */
            <KhoaTheoTuyen qd={qdBatTat} co="canControl" hienHuyHieu={false}>
              <Button
                size="sm"
                variant="secondary"
                icon={<RotateCcw className="h-4 w-4" />}
                loading={release.isPending}
                disabled={!online || !canControl}
                onClick={() => release.mutate()}
                title="Bỏ giữ tay, trả quyền cho lịch tự động của tủ"
              >
                Trả quyền về tự động
              </Button>
            </KhoaTheoTuyen>
          }
        >
          {/* Một băng cho CẢ khối — thay vì lặp câu ở từng ngõ ra (lee góp ý 10/09). */}
          <BangKhoaTuyen qd={qdBatTat} co="canControl" className="mb-3" />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {c.outputs.map((o) => (
              <OutputCard
                key={o.id}
                output={o}
                nguonGiuSong={nguonGiuSong(o.index)}
                online={online}
                /*
                 * ➕ `GD-09b` — TRUYỀN QUYẾT ĐỊNH, KHÔNG TRUYỀN `boolean`. Thẻ ngõ ra vẽ một
                 *   huy hiệu hổ phách khi thiếu quyền; với một `boolean` nó không phân biệt
                 *   được "đã đo, không có quyền" với "chưa đo được", nên nó khai bừa ở ca thứ
                 *   hai. Ép kiểu ở ĐÂY là ép cả chỗ vẽ phải nghĩ đến ba ca.
                 */
                qd={qdBatTat}
                busy={control.isPending && control.variables?.index === o.index}
                onToggle={(on) => control.mutate({ index: o.index, on })}
              />
            ))}
          </div>
          {/*
            * ⚠ Dải tóm tắt nay đếm theo `nhanGiuNgo()`, KHÔNG đếm theo `manualHold` một mình —
            *   nếu không thì sau `P-2` một ngõ do MÀN HÌNH giữ mà `manualHold = false` sẽ
            *   **biến mất khỏi dải này**, đúng lúc nó cần được nói ra nhất.
            */}
          {c.outputs.some((o) => nhanGiuNgo(o.nguonGiuQuyen, o.manualHold).kieu !== 'trong') && (
            <p className="mt-3 flex items-center gap-1.5 text-xs text-canh">
              <Hand className="h-3.5 w-3.5" />
              {/* TK-24: một câu ngắn, trạng thái + việc cần làm (tệp 29 điều 6). Nguồn giữ đã ghi trên từng thẻ ngõ. */}
              Ngõ bị giữ: lịch không chạm vào. Trả quyền tại nguồn đang giữ.
            </p>
          )}

          {/*
            * ═══════════════════════════════════════════════════════════════════════════════
            * ➕ `GD-03` — BIỂU ĐỒ VỀ CHUNG THẺ VỚI ĐIỀU KHIỂN (mẫu `23`).
            *
            * 🔴 Vì sao chung thẻ chứ không hai thẻ cạnh nhau: hai thứ này trả lời cùng một câu
            *   hỏi ở hai thang thời gian — *"ngõ ra đang thế nào"* và *"24 giờ qua nó thế
            *   nào"*. Người bấm tắt một ngõ rồi nhìn xuống thấy đường công suất tụt: đó là
            *   nhân chứng cho việc mình vừa làm. Tách hai thẻ là bắt họ so bằng trí nhớ.
            * ⚠ `hours` GIỮ NGUYÊN state cũ (6/24/72) — L-phá của phiếu đo đúng chỗ này.
            * ═══════════════════════════════════════════════════════════════════════════════
            */}
          <div className="mt-4 border-t border-vien pt-3">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="flex items-center gap-1.5 text-xs text-chu-2">
                <i className="inline-block h-[3px] w-2.5 rounded-sm bg-nhan" />Công suất pha 1{donViP ? ` (${donViP})` : ''}
              </span>
              <span className="flex items-center gap-1.5 text-xs text-chu-2">
                <i className="inline-block h-[3px] w-2.5 rounded-sm bg-tot" />Điện áp pha 1 (V)
              </span>
              <span className="flex items-center gap-1.5 text-xs text-chu-2">
                <i className="inline-block h-[3px] w-2.5 rounded-sm bg-canh" />Dòng pha 1 (A, trục phải)
              </span>
              <div className="ml-auto flex items-center gap-1">
                {[6, 24, 72].map((h) => (
                  <Button
                    key={h}
                    size="sm"
                    variant={hours === h ? 'primary' : 'secondary'}
                    onClick={() => setHours(h)}
                  >
                    {h} giờ
                  </Button>
                ))}
              </div>
            </div>

            <div className="w-full">
              {meter.isLoading && <div className="h-56"><Spinner label="Đang tải biểu đồ…" /></div>}
              {meter.isError && <ErrorBox error={meter.error} onRetry={() => void meter.refetch()} />}
              {/* TK-20 d) cửa sổ không có mẫu nào ⇒ NÓI RA, không vẽ một đường từ khoảng khác. */}
              {meter.data && meter.data.soOCoMau === 0 && (
                <EmptyState
                  icon={<Gauge className="h-8 w-8" />}
                  title={CAU_TRONG(hours)}
                  hint="Công tơ chưa gửi số đo nào."
                />
              )}
              {meter.data && meter.data.soOCoMau > 0 && (
                <>
                  {/* TK-20 A: nói rõ mẫu MỚI NHẤT lúc nào — biểu đồ không được trông "hiện tại" khi nó cũ. */}
                  <p className="mb-1 text-xs text-chu-3">
                    {fmtDateTime(meter.data.tu)} → {fmtDateTime(meter.data.den)} · mẫu mới nhất{' '}
                    {meter.data.mauCuoi ? `${fmtTime(meter.data.mauCuoi)} (${fmtAgo(meter.data.mauCuoi)})` : '—'}
                    {/*
                      * 🔴 09/10/2026 — lee khoanh đỏ dòng này. Bỏ hai vế GIẢI THÍCH CÁCH VẼ
                      *   ("N/M ô có số", "dải mờ = thấp nhất–cao nhất…"): đó là chú giải của
                      *   biểu đồ, không phải một câu văn, và nó dài gấp đôi phần có tin.
                      *   Giữ lại khoảng thời gian và mẫu mới nhất — hai thứ trả lời câu
                      *   "số này của lúc nào".
                      */}
                  </p>
                  {/* TK-20 c) MỖI ĐẠI LƯỢNG MỘT TRỤC (lib/bieudo.ts TRUC_CUA). Trên: điện áp. Dưới: công suất (trái) + dòng (phải). */}
                  {[
                    { duong: ['v'] as const, cao: 'h-28' },
                    { duong: ['p', 'i'] as const, cao: 'h-32' },
                  ].map((bd) => (
                    <div key={bd.duong.join('')} className={`${bd.cao} w-full`}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={diemDo} margin={{ top: 6, right: 4, bottom: 0, left: -10 }}>
                          <CartesianGrid stroke="rgb(var(--vien))" strokeDasharray="3 3" />
                          {/* Trục thời gian THẬT, phủ đủ cửa sổ (TK-20 B) — không phải trục theo thứ tự dòng. */}
                          <XAxis dataKey="ms" type="number" scale="time"
                                 domain={[new Date(meter.data!.tu).getTime(), new Date(meter.data!.den).getTime()]}
                                 tickFormatter={(v: number) => fmtTime(v)} minTickGap={28}
                                 tick={{ fontSize: 12, fill: 'rgb(var(--chu-2))' }} />
                          {bd.duong.map((k, i) => {
                            const m = mienTruc(diemDo, TRUC_CUA[k]);
                            return (
                              <YAxis key={k} yAxisId={TRUC_CUA[k]} orientation={i === 0 ? 'left' : 'right'}
                                     domain={m ?? ['auto', 'auto']} width={BE_RONG_TRUC_Y}
                                     tickFormatter={(v: number) => fmtNum(v, chuSo[k])}
                                     tick={{ fontSize: 12, fill: NHAN_DUONG[k].mau }} />
                            );
                          })}
                          <Tooltip
                            contentStyle={{
                              fontSize: 12, borderRadius: 8, border: '1px solid rgb(var(--vien))',
                              background: 'rgb(var(--nen-1))', color: 'rgb(var(--chu-1))',
                            }}
                            itemStyle={{ color: 'rgb(var(--chu-1))' }}
                            labelStyle={{ color: 'rgb(var(--chu-2))' }}
                            labelFormatter={(v: any) => fmtDateTime(Number(v))}
                            formatter={(v: any, name: any) => {
                              const k = (Object.keys(NHAN_DUONG) as DuongDo[]).find((x) => String(name).startsWith(NHAN_DUONG[x].ten));
                              const cs = k ? chuSo[k] : 1;
                              const so = v == null ? '—' : Array.isArray(v) ? `${fmtNum(v[0], cs)} – ${fmtNum(v[1], cs)}` : fmtNum(Number(v), cs);
                              return [so, k === 'p' && donViP ? `${name} (${donViP})` : name];
                            }}
                          />
                          {/*
                            * TK-23 — DẢI min–max của từng ô vẽ NỀN MỜ, đường avg ĐẬM vẽ SAU (nằm trên). Ô 5 phút có đợt vọt
                            *   2 phút: avg 15 kW, nhưng mép trên của dải chạm 23 kW — người nhìn thấy ngay ô nào có đỉnh.
                            * ⛔ Không `connectNulls` cho dải lẫn đường (TK-20 a).
                            */}
                          {bd.duong.map((k) => (
                            <Area key={`dai-${k}`} yAxisId={TRUC_CUA[k]} type="linear" dataKey={DAI_CUA[k]}
                                  name={`${NHAN_DUONG[k].ten} · thấp–cao trong ô`}
                                  stroke="none" fill={NHAN_DUONG[k].mau} fillOpacity={0.18}
                                  activeDot={false} isAnimationActive={false} />
                          ))}
                          {/* ⛔ KHÔNG `connectNulls`: ô NULL (không tải / không mẫu) là KHOẢNG TRỐNG, không phải 0 (TK-20 a). */}
                          {bd.duong.map((k) => (
                            <Line key={k} yAxisId={TRUC_CUA[k]} type="linear" dataKey={k} name={NHAN_DUONG[k].ten}
                                  stroke={NHAN_DUONG[k].mau} strokeWidth={k === 'p' ? 2 : 1.5} dot={false}
                                  isAnimationActive={false} />
                          ))}
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </Card>

        {/* ── phải: CẦN CHÚ Ý + THÔNG TIN TỦ ── */}
        <div className="flex flex-col gap-3">
          {/*
            * ═══════════════════════════════════════════════════════════════════════════════
            * ➕ `GD-03` — THẺ "CẦN CHÚ Ý" GOM BỐN THỨ TỪNG NẰM RẢI Ở BỐN CHỖ:
            *     ELR (đáy khối cài đặt) · lớp bảo vệ (giữa khối cài đặt) ·
            *     cảnh báo (cuối trang) · sức khoẻ tủ (một thẻ riêng giữa trang)
            *
            * 🔴 Bốn thứ ấy trả lời CÙNG MỘT câu hỏi — *"có gì đang không ổn không?"* — và
            *   bản cũ bắt người trực đi bốn chỗ để ghép câu trả lời. Ai cũng sẽ có ngày quên
            *   một chỗ, và chỗ quên là chỗ hỏng.
            * ⛔ Ba màu, ba nghĩa, đúng luật `[95]`: lá = đã đo và ổn · hổ phách = cần người
            *   xử lý · XÁM = **chưa đo được** (không phải "ổn"). Không màu đỏ ở đây: đỏ dành
            *   cho sự cố đang chạy, và nó đã có chỗ riêng (dải mức 1 toàn cục + băng mất kết
            *   nối ngay trên).
            * ═══════════════════════════════════════════════════════════════════════════════
            */}
          <Card title="Cần chú ý">
            <ul className="space-y-1.5">
              <DongChuY d={tomTatELR(ct)} />
              <DongChuY d={tomTatLopBaoVe(ct)} phai={
                <Button size="sm" variant="ghost" onClick={() => { void ct.doc(); void ct.docLive(); }} disabled={!online}>
                  Đọc lại
                </Button>
              } />
              <DongChuY
                d={
                  alarms.isLoading
                    ? { mau: 'chua-ro', cau: 'Cảnh báo đang hoạt động: đang tải', phu: '' }
                    : (alarms.data?.length ?? 0) === 0
                      ? { mau: 'tot', cau: 'Cảnh báo đang hoạt động: 0', phu: '' }
                      : {
                        mau: 'canh',
                        cau: `Cảnh báo đang hoạt động: ${alarms.data!.length}`,
                        phu: `${nhanCanhBao(alarms.data![0].code)} · ${fmtAgo(alarms.data![0].raisedAt)}`,
                      }
                }
                phai={
                  (alarms.data?.length ?? 0) > 0
                    ? <Badge tone="red">{alarms.data!.length}</Badge>
                    : undefined
                }
              />
              {/* Sức khoẻ tủ — `P-6` `A.7`. Nay là MỘT DÒNG + nút, chi tiết mở trong Dialog. */}
              <DongSucKhoe deviceId={c.deviceId} online={online} />
            </ul>
          </Card>

          {/*
            * ⛔ LƯỚI 6 Ô, KHÔNG PHẢI BẢNG 11 DÒNG — và bốn dòng bị rút (trạng thái · công tắc
            *   · giao thức · firmware) KHÔNG mất: chúng lên huy hiệu đầu trang. Rút mà không
            *   chuyển đi đâu mới là giấu.
            * ⚠ "Lệch giờ RTC" và "giờ mặt trời" cũng rời khỏi đây — chúng về ô số ④ (đồng hồ),
            *   nơi chúng có nghĩa cạnh chính cái đồng hồ.
            */}
          <Card title="Thông tin tủ">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <OThongTin nhan="Tuyến">
                {c.route ? c.route.tenDayDu : 'Chưa gán tuyến — vào trang “Tuyến chiếu sáng” để gán'}
              </OThongTin>
              <OThongTin nhan="Mã thiết bị">
                {c.deviceId ? <MaThietBi ma={c.deviceId} /> : <span className="text-chu-3">chưa đăng ký</span>}
              </OThongTin>
              {/*
                * 🔴 09/10/2026 — lee khoanh đỏ hai ô này. "· 11 giây trước" lặp lại đúng cái
                *   giờ ngay bên trái; "chưa có" là một câu ở chỗ đáng lẽ chỉ cần một dấu gạch.
                *   Giờ tương đối chuyển vào `title`, ai cần thì rê chuột.
                */}
              <OThongTin nhan="Thấy lần cuối">
                <span title={fmtAgo(c.lastSeenAt)}>{fmtDateTime(c.lastSeenAt)}</span>
              </OThongTin>
              <OThongTin nhan="Sóng RSSI">
                {c.rssi != null ? `${c.rssi} dBm` : <span className="text-chu-3">—</span>}
              </OThongTin>
              <OThongTin nhan="Toạ độ">
                {c.lat != null && c.lng != null ? (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5 text-chu-3" />
                    {c.lat.toFixed(5)}, {c.lng.toFixed(5)}{c.gpsFix ? '' : ' (chưa bắt được GPS)'}
                  </span>
                ) : <span className="text-chu-3">chưa có</span>}
              </OThongTin>
              <OThongTin nhan="Công tắc vật lý">
                {switchPos ? SWITCH_LABEL[switchPos] : <span className="text-chu-3">chưa rõ</span>}
              </OThongTin>
            </div>
            {c.address && <p className="mt-2 text-xs text-chu-2">{c.address}</p>}
          </Card>
        </div>
      </div>

      {/* ══════════ HÀNG 4 · NHẬT KÝ LỆNH │ NHẬT KÝ TỦ GỬI LÊN ══════════ */}
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-[2fr_1fr]">
        <Card
          title="Nhật ký lệnh gửi xuống tủ"
          subtitle="10 dòng gần nhất"
          actions={
            <div className="flex items-center gap-2">
              {/*
                * ⚠ "Xem tất cả" CHỈ HIỆN KHI CÓ GÌ ĐỂ XEM THÊM. Một nút mở ra đúng cái bảng
                *   vừa đọc là một nút dạy người dùng rằng nút ở đây không làm gì.
                */}
              {(commands.data?.length ?? 0) > 10 && (
                <Button size="sm" variant="ghost" onClick={() => setXemHetLenh(true)}>
                  Xem tất cả ({commands.data!.length})
                </Button>
              )}
              <Button size="sm" icon={<RefreshCw className="h-4 w-4" />} onClick={() => void commands.refetch()} loading={commands.isFetching}>
                Làm mới
              </Button>
            </div>
          }
        >
          {commands.isLoading && <Spinner />}
          {commands.data && commands.data.length === 0 && (
            <EmptyState icon={<Terminal className="h-8 w-8" />} title="Chưa có lệnh nào được gửi cho tủ này" />
          )}
          {commands.data && commands.data.length > 0 && (
            <BangLenh ds={commands.data.slice(0, 10)} kieuXacNhan={false} />
          )}
        </Card>

        <Card title="Nhật ký do tủ gửi lên">
          {logs.isLoading && <Spinner />}
          {logs.data && logs.data.length === 0 && (
            <EmptyState icon={<Activity className="h-8 w-8" />} title="Tủ chưa gửi dòng nhật ký nào" />
          )}
          {logs.data && logs.data.length > 0 && (
            /*
              * GD-14 mục 4 — mặt trước: GIỜ · CÂU NGƯỜI DÙNG (`dongNhatKy`). Thẻ `[ACK-…]`,
              *   `requestId`, `epochMs`, `tz` chỉ còn trong "Chi tiết kỹ thuật" bên dưới, và
              *   chỉ Quản trị hệ thống thấy khối ấy.
              */
            <ul className="thin-scroll max-h-72 space-y-1 overflow-y-auto text-sm" data-nhat-ky-tu>
              {logs.data.map((l) => (
                <li key={l.id} className="flex gap-2 border-b border-vien py-1">
                  <span className="shrink-0 whitespace-nowrap tabular-nums text-chu-3" title={fmtDateTimeSec(l.at)}>
                    {gioNgay(l.at)}
                  </span>
                  <CauNguoi loi={dongNhatKy(l.tag, l.message)} className="min-w-0 break-words text-chu-1" />
                </li>
              ))}
            </ul>
          )}
          {logs.data && logs.data.length > 0 && (
            <ChiTietKyThuat>
              <ul className="space-y-1 font-mono text-xs">
                {logs.data.map((l) => (
                  <li key={l.id} className="break-words">
                    {fmtDateTimeSec(l.at)} {l.tag ? `[${l.tag}] ` : ''}{l.message}
                  </li>
                ))}
              </ul>
            </ChiTietKyThuat>
          )}
        </Card>
      </div>

      {/* ══════════ HÀNG 5 · KHỐI THU-MỞ "CÀI ĐẶT TỦ" ══════════ */}
      <CaiDatThietBi
        ct={ct}
        coTheGhi={coTheGhiCaiDat}
        mo={moCaiDat}
        onDoiMo={() => doiMoCaiDat(!moCaiDat)}
      />

      {/*
        * ⚠ HỘP "XEM TẤT CẢ" — và CỘT "KIỂU XÁC NHẬN" CHỈ CÓ Ở ĐÂY (phiếu chốt). Nó trả lời
        *   một câu hỏi của người sửa lỗi (*"tủ xác nhận tường minh hay máy chủ suy đoán?"*),
        *   không phải của người trực ca; để nó ở bảng 10 dòng là lấy mất một cột của thứ
        *   người trực đọc hằng ngày.
        */}
      <XemHetLenh
        mo={xemHetLenh}
        onDong={() => setXemHetLenh(false)}
        ds={commands.data ?? []}
      />

      {/*
        * 🔴 KHỐI CẢNH BÁO XUỐNG CUỐI — YC-31 mục 4, và CỐ Ý làm SAU mục 1.
        *   Đẩy nó xuống trước khi sửa cửa dựng cảnh báo là đem 100 hàng giấu vào chỗ ít ai
        *   cuộn tới — cách tệ nhất để xử lý một cảnh báo là để nó ở nơi không ai nhìn.
        *   Nay một lần lệch = một hàng, và lịch khớp lại thì hàng ấy tự đóng, nên khối này
        *   tự gọn; xuống cuối mới là dọn chứ không phải giấu.
        *
        * ⚠ Thứ tự lee muốn: Đồng hồ · Trạng thái sống · Cài đặt · Nhật ký lệnh · Cảnh báo.
        *   Và mức 1 VẪN lên dải toàn cục — chỗ đó mới là chỗ nó phải kêu (L52).
        */}
      {/* Cảnh báo của tủ */}
      <Card
        title="Cảnh báo đang hoạt động của tủ"
        actions={alarms.data?.length ? <Badge tone="red">{alarms.data.length}</Badge> : undefined}
      >
        {alarms.isLoading && <Spinner />}
        {alarms.data && alarms.data.length === 0 && (
          <p className="text-sm text-chu-2">Không có cảnh báo nào đang hoạt động.</p>
        )}
        {alarms.data && alarms.data.length > 0 && (
          // Cùng một chỗ hở với trang Tổng quan, cùng một cách bịt — sửa một chỗ mà bỏ
          // chỗ kia thì lần sau người ta lại gặp nó ở đúng nửa còn lại.
          <ul className="cuon-doc space-y-2 pr-1">
            {alarms.data.map((a) => (
              <li key={a.id} className="flex items-start gap-2 rounded-lg border border-nguy/50 bg-nguy-nen p-2.5 text-sm">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-nguy" />
                <div className="min-w-0">
                  <p className="font-medium text-nguy">
                    {nhanCanhBao(a.code)}{a.output ? ` — ngõ ra ${a.output}` : ''}
                    {/* Mã gốc vẫn hiện: người vận hành đọc câu tiếng Việt, người sửa lỗi cần đúng chuỗi để tìm trong log. */}
                    <span className="ml-2 font-mono text-xs font-normal text-nguy">{a.code}</span>
                  </p>
                  {a.message && <p className="text-xs text-nguy">{a.message}</p>}
                  <p className="mt-0.5 text-xs text-nguy">Từ {fmtDateTime(a.raisedAt)} ({fmtAgo(a.raisedAt)})</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

    </div>
  );
}

/* ------------------------------------------------------------ bộ phận con */

/**
 * Khối "chưa có số liệu" cho một Ô SỐ — và nó phải NÓI RA LÝ DO.
 *
 * ⛔ Một ô trống không giải thích thì người trực tự điền lý do trong đầu, và thường điền sai
 *   ("chắc tủ không có công tơ"). Câu `cauKhongVe` phân biệt bốn ca: máy chủ đời cũ · số đo
 *   quá tuổi · hết hạn ngay trên màn hình · chưa có mẫu nào.
 */
/**
 * TK-22 B — ngưỡng CẢNH BÁO quá dòng (A) của MỘT tủ. Trống = không cảnh báo (mặc định, không có số
 * mặc định cứng). Máy chủ tự xét mỗi khung đo; CHỈ BÁO — cắt là việc của cổng bảo vệ ở tủ (`oc_set`,
 * một ngưỡng KHÁC, đặt trong "Cài đặt thiết bị"). Ghi = tick `doi_nguong`.
 */
function TheNguongQuaDong({ cabinetId, nguongA, dongA }: { cabinetId: string; nguongA: number | null; dongA: number | null }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [nhap, setNhap] = useState(nguongA === null ? '' : String(nguongA));
  useEffect(() => { setNhap(nguongA === null ? '' : String(nguongA)); }, [nguongA]);
  const so = nhap.trim() === '' ? null : Number(nhap.replace(',', '.'));
  const hopLe = so === null || (Number.isFinite(so) && so > 0 && so <= 2000);
  const luu = useMutation({
    mutationFn: (v: number | null) => api.put<{ ok: boolean; daDongCanhBao: number }>(
      `/api/cabinets/${cabinetId}/nguong-qua-dong`, { nguongA: v }),
    onSuccess: (r, v) => {
      toast.success(v === null ? 'Đã TẮT cảnh báo quá dòng của tủ này' : `Ngưỡng cảnh báo quá dòng: ${v} A`,
        v === null && r.daDongCanhBao > 0 ? 'Cảnh báo quá dòng đang mở đã được đóng.' : undefined);
      qc.invalidateQueries({ queryKey: qk.cabinet(cabinetId) });
    },
    onError: (e) => toast.error('Không lưu được ngưỡng', errorMessage(e)),
  });
  return (
    <div className="space-y-2 text-xs text-chu-2">
      <p>
        {nguongA === null
          ? 'Chưa đặt ngưỡng — không cảnh báo quá dòng.'
          : <>Máy chủ báo khi dòng vượt <b className="text-chu-1">{`${fmtNum(nguongA, 1)} A`}</b>{` (đóng lại khi xuống dưới ${fmtNum(nguongA * 0.95, 1)} A).`}</>}
        {dongA !== null && <> Dòng hiện tại: <b className="text-chu-1">{`${fmtNum(dongA, 1)} A`}</b>.</>}
      </p>
      {/*
        * 🔴 09/10/2026 — lee khoanh đỏ ô này. GIỮ VẾ AN TOÀN, BỎ VẾ GIẢNG GIẢI.
        *   "Chỉ cảnh báo, không cắt điện" là câu DUY NHẤT ngăn người đọc tin rằng ngưỡng này
        *   sẽ cắt điện — bỏ nó đi là bỏ một lời cảnh báo, không phải bỏ một dòng rườm.
        *   Vế "đặt theo tải thật của tủ này" chuyển vào `title` của ô nhập, đúng chỗ người ta
        *   cần nó: lúc đang gõ con số.
        */}
      <p className="text-chu-3">Chỉ cảnh báo, không cắt điện.</p>
      <KhoaTheoTick khoa="doi_nguong">
        <div className="flex items-center gap-2">
          <input className="inp w-28" inputMode="decimal" value={nhap} placeholder="trống = tắt"
                 title="Đặt theo tải thật của tủ này — mỗi tủ một ngưỡng"
                 aria-label="Ngưỡng cảnh báo quá dòng (A)" onChange={(e) => setNhap(e.target.value)} />
          <span>A</span>
          <Button size="sm" variant="primary" loading={luu.isPending} disabled={!hopLe || so === nguongA}
                  onClick={() => luu.mutate(so)}>Lưu</Button>
        </div>
      </KhoaTheoTick>
      {!hopLe && <p className="text-canh">Nhập một số dương, tối đa 2 000 A — hoặc để trống để tắt.</p>}
    </div>
  );
}

function ChuaCoSo({ cau }: { cau: string }) {
  return (
    <div>
      <p className="text-sm font-semibold text-chu-1">Chưa có số liệu</p>
      <p className="mt-0.5 text-xs leading-relaxed text-chu-3">{cau}</p>
    </div>
  );
}

/** Một ô của lưới "Thông tin tủ" — nhãn nhỏ hoa, giá trị dưới. */
function OThongTin({ nhan, children }: { nhan: string; children: ReactNode }) {
  return (
    <div className="min-w-0 rounded-nut bg-nen-2 px-2.5 py-1.5">
      <p className="text-xs uppercase tracking-wide text-chu-3">{nhan}</p>
      <p className="truncate text-[12.5px] text-chu-1" title={typeof children === 'string' ? children : undefined}>
        {children}
      </p>
    </div>
  );
}

/**
 * Một dòng của thẻ "Cần chú ý": chấm màu · câu · câu phụ · (chỗ cho nút bên phải).
 *
 * ⛔ BA MÀU, BA NGHĨA — luật `[95]`, và cái XÁM là cái hay bị nuốt nhất: *chưa đo được* KHÔNG
 *   phải *ổn*. Một dòng xám đọc ra "tôi chưa biết", và đó là một câu trả lời hợp lệ; vẽ nó
 *   màu lá là dựng một kết luận từ chỗ không có dữ liệu.
 */
function DongChuY({ d, phai }: { d: DongCanChuY; phai?: ReactNode }) {
  const mau = d.mau === 'tot' ? 'bg-tot' : d.mau === 'canh' ? 'bg-canh' : 'bg-chua-ro';
  return (
    <li className="flex items-center gap-2.5 rounded-nut bg-nen-2 px-2.5 py-2">
      <span className={`h-2 w-2 shrink-0 rounded-full ${mau}`} />
      <span className="min-w-0 flex-1">
        <span className="block text-[12.5px] text-chu-1">{d.cau}</span>
        {d.phu && <span className="block text-xs leading-snug text-chu-2">{d.phu}</span>}
        {/* GD-14 vòng 3 — tên trường / số hex của dòng này: chỉ Quản trị, gập lại. */}
        {d.kyThuat && (
          <ChiTietKyThuat><pre className="whitespace-pre-wrap break-words font-mono text-xs">{d.kyThuat}</pre></ChiTietKyThuat>
        )}
      </span>
      {phai}
    </li>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * Ô SỐ ④ — ĐỒNG HỒ TỦ. Và đây là chỗ tôi **cố ý làm khác mẫu**, nói ra để lee bác được.
 *
 * 🔴 Mẫu `_BUS/23` vẽ `14:43:57` — một cái đồng hồ đang CHẠY. Nhưng số này không chạy: nó là
 *   một ẢNH CHỤP, lấy về bằng một lệnh hỏi thẳng tủ (`/api/lenh/trangthai`), và nó đứng im
 *   cho tới lần đọc sau. Vẽ một ảnh chụp dưới hình dạng một cái đồng hồ là **nói dối bằng
 *   hình dạng**: người đọc sẽ tin con số ấy là giờ tủ NGAY LÚC NÀY.
 * ⇒ Vẽ giờ tủ + **mốc đã đọc nó lúc nào**. Cùng một luật với `soDo` (số đo có tuổi), với
 *   "sức khoẻ tủ mang theo mốc giờ đọc", và với `Metric` mức "chưa có số liệu".
 *
 * ⚠ Cộng theo `tzOffsetMin` TỦ BÁO, không theo múi giờ trình duyệt: đây là giờ CỦA TỦ, và
 *   tủ có thể đang đặt sai múi — đúng thứ ta cần nhìn thấy. Tủ chưa báo múi ⇒ KHÔNG đoán +7.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
function DongHoTu({ gio, c }: { gio: { echo: any; gioMayChuMs: number } | null; c: { sunriseMin: number | null; sunsetMin: number | null } }) {
  const st = gio?.echo ?? {};
  const tuMs = Number(st.deviceTimeMs ?? 0) || 0;
  const coTz = Object.prototype.hasOwnProperty.call(st, 'tzOffsetMin');
  const tz = Number(st.tzOffsetMin ?? 0) || 0;
  const matTroi = c.sunriseMin != null && c.sunsetMin != null
    ? `mặt trời mọc ${minToStr(c.sunriseMin)} · lặn ${minToStr(c.sunsetMin)}`
    : 'chưa có giờ mặt trời';

  if (!gio || !tuMs) {
    return (
      <ChuaCoSo cau="Chưa đọc đồng hồ tủ. Mở “Cài đặt tủ” → “Đọc lại”." />
    );
  }
  const iso = new Date(tuMs + (coTz ? tz * 60_000 : 0)).toISOString();
  return (
    <>
      <p className="chu-tieude text-[20px] font-semibold text-chu-1">
        {coTz ? iso.slice(11, 19) : <span className="text-[15px]">chưa quy ra giờ địa phương</span>}
      </p>
      <p className="mt-1 text-xs leading-relaxed text-chu-3">
        {coTz ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)} · ` : ''}
        UTC {new Date(tuMs).toISOString().slice(11, 19)}
        {!coTz && ' · tủ chưa báo múi giờ'}
        <br />
        {matTroi}
        {/*
          * ⛔ MỐC ĐỌC LÀ BẮT BUỘC, không phải chi tiết thừa: xem khối chú thích trên hàm.
          */}
        {gio.gioMayChuMs ? ` · đọc lúc ${fmtTime(gio.gioMayChuMs)}` : ''}
      </p>
    </>
  );
}

/** Tuổi số đo thành câu ngắn cho góc phải ô điện áp. */
function moTaTuoi(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return 'chưa rõ';
  if (ms < 1000) return `${Math.round(ms)} ms trước`;
  if (ms < 60_000) return `${Math.round(ms / 1000)} giây trước`;
  return `${Math.round(ms / 60_000)} phút trước`;
}

/**
 * Bảng nhật ký lệnh — dùng CHUNG cho bảng 10 dòng ở trang và bảng đầy đủ trong hộp.
 *
 * ⛔ MỘT BẢNG, HAI CHỖ GỌI. Chép nó ra hai bản là hai bản sẽ trôi khỏi nhau, và chỗ trôi sẽ
 *   là chỗ ít người mở (cái trong hộp) — tức là chỗ không ai phát hiện.
 * ⚠ `kieuXacNhan` là khác biệt DUY NHẤT giữa hai chỗ, và nó là một tham số chứ không phải một
 *   bản sao: cột ấy trả lời câu hỏi của người SỬA LỖI, không của người trực ca.
 */
function BangLenh({ ds, kieuXacNhan }: { ds: any[]; kieuXacNhan: boolean }) {
  return (
    /*
     * ⛔ `tbl-wrap cuon-doc` + `tbl tbl-cuon` — CẶP BẮT BUỘC của dự án, và mục `[25]` canh cả
     *   hai vế: một khung cuộn mà mất hàng tiêu đề là đổi một lỗi lấy một lỗi khác. Bản đầu
     *   tôi viết `tbl-wrap` trần trụi (bảng ở trang chỉ 10 dòng nên "đã bị chặn bởi dữ liệu"),
     *   và phép ấy đỏ — đúng: **nó không nhìn thấy `slice(0, 10)`**, và một cái trần do dữ
     *   liệu thì mất ngay ngày ai đó đổi con số 10.
     */
    <>
    <div className="tbl-wrap cuon-doc">
      {/*
        * GD-14 — ba cột người đọc: THỜI ĐIỂM · NỘI DUNG · TRẠNG THÁI. Cột "Lệnh" (mã `kind`)
        *   và cột "Ghi chú" (chữ tủ trả về, không dấu) rời mặt trước: mã vào "Chi tiết kỹ thuật",
        *   ghi chú được DỊCH rồi gộp vào ô trạng thái cạnh huy hiệu.
        */}
      <table className="tbl tbl-cuon" style={{ tableLayout: 'fixed', width: '100%' }} data-bang-lenh>
        <colgroup>
          <col style={{ width: '112px' }} />
          <col />
          <col style={{ width: '42%' }} />
          {kieuXacNhan && <col style={{ width: '130px' }} />}
        </colgroup>
        <thead>
          <tr>
            <th>Thời điểm</th>
            <th>Nội dung</th>
            <th>Trạng thái</th>
            {kieuXacNhan && <th>Kiểu xác nhận</th>}
          </tr>
        </thead>
        <tbody>
          {ds.map((cmd) => {
            const kq = ketQuaLenh(cmd.state, cmd.detail);
            return (
              <tr key={cmd.id}>
                {/* `HH:mm dd/MM` không bao giờ xuống dòng; ngày giờ đầy đủ trong `title`. */}
                <td className="whitespace-nowrap tabular-nums text-chu-2" title={fmtDateTimeSec(cmd.createdAt)} data-o="thoi-diem">
                  {gioNgay(cmd.createdAt)}
                </td>
                <td className="break-words text-chu-1" data-o="noi-dung">
                  <CauNguoi loi={noiDungLenh(cmd.kind, cmd.payload, moTaTaiLenh(cmd.payload))} />
                </td>
                <td data-o="trang-thai">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <CommandBadge state={cmd.state} />
                    {kq && <CauNguoi loi={kq} className="break-words text-xs text-chu-2" />}
                  </div>
                </td>
                {kieuXacNhan && (
                  <td className="whitespace-nowrap text-xs text-chu-2">
                    {cmd.ackMode === 'EXPLICIT' ? 'Tủ xác nhận' : 'Máy chủ suy ra'}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    {/*
      * ⛔ Nguyên văn máy — `kind`, payload JSON, chữ tủ trả về — CHỈ ở đây, chỉ Quản trị thấy.
      *   Dòng dài thì cắt "…" và `title` mang nguyên văn: một dòng cụt không đối chiếu được.
      */}
    <ChiTietKyThuat>
      <table className="w-full font-mono text-xs" style={{ tableLayout: 'fixed' }}>
        <tbody>
          {ds.map((cmd) => (
            <tr key={cmd.id} className="border-b border-vien align-top">
              <td className="w-40 whitespace-nowrap py-0.5 pr-2 text-chu-3">{fmtDateTimeSec(cmd.createdAt)}</td>
              <td className="w-36 truncate py-0.5 pr-2">{cmd.kind}</td>
              <td className="truncate py-0.5 pr-2" title={JSON.stringify(cmd.payload)}>{JSON.stringify(cmd.payload)}</td>
              <td className="w-24 whitespace-nowrap py-0.5 pr-2">{cmd.state} · {cmd.ackMode}</td>
              <td className="truncate py-0.5" title={cmd.detail ?? ''}>{cmd.detail ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ChiTietKyThuat>
    </>
  );
}

/** `HH:mm dd/MM` — giờ Việt Nam (cùng `TZ` của `format.ts`). */
function gioNgay(v: string | null | undefined): string {
  return v ? `${fmtTime(v)} ${fmtDate(v).slice(0, 5)}` : '—';
}

/** Câu người đọc; mã CHƯA dịch thì đứng cạnh thành chip — không đoán nghĩa hộ nó. */
function CauNguoi({ loi, className }: { loi: LoiNguoi; className?: string }) {
  return (
    <span className={className}>
      {loi.cau}
      {loi.ma && (
        <code data-chip-ma className="ml-1 rounded border border-vien px-1 font-mono text-xs text-chu-2">{loi.ma}</code>
      )}
    </span>
  );
}

/**
 * GD-14 mục 6 — mã thiết bị là định danh tủ trên máy chủ. Không phải Quản trị: dạng che
 *   `DEV-…Q0iMP`, KHÔNG có nút chép. Quản trị: đủ + nút chép.
 */
function MaThietBi({ ma }: { ma: string }) {
  const laQuanTri = useLaQuanTri();
  const [daChep, datDaChep] = useState(false);
  if (!laQuanTri) return <span className="tabular-nums">{cheMaThietBi(ma)}</span>;
  return (
    <span className="inline-flex items-center gap-1">
      <span className="break-all">{ma}</span>
      <button
        type="button"
        aria-label="Chép mã thiết bị"
        title={daChep ? 'Đã chép' : 'Chép mã thiết bị'}
        className="rounded p-0.5 text-chu-3 hover:bg-nen-2 hover:text-chu-1"
        onClick={() => {
          void navigator.clipboard?.writeText(ma).then(() => {
            datDaChep(true);
            setTimeout(() => datDaChep(false), 1500);
          }).catch(() => {});
        }}
      >
        {daChep ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </span>
  );
}

/** Hộp "xem tất cả" — bảng đầy đủ, và ĐÂY là chỗ duy nhất có cột "Kiểu xác nhận". */
function XemHetLenh({ mo, onDong, ds }: { mo: boolean; onDong: () => void; ds: any[] }) {
  return (
    <Modal
      open={mo}
      onClose={onDong}
      wide
      title="Nhật ký lệnh gửi xuống tủ"
      subtitle="Cột “Kiểu xác nhận” cho biết tủ tự xác nhận hay máy chủ chỉ suy ra từ trạng thái tủ báo lên"
    >
      {/*
        * ⛔ KHÔNG bọc thêm một trần thứ hai ở đây: `BangLenh` đã có `cuon-doc`, và thân
        *   `Modal` của `ui.tsx` cũng tự cuộn. Ba khung cuộn lồng nhau thì con lăn chuột ăn
        *   vào cái trong cùng, người dùng tưởng bảng đã hết. Mục `[25]` có hẳn một phép cho
        *   chiều ngược này.
        */}
      <BangLenh ds={ds} kieuXacNhan />
    </Modal>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * SỨC KHOẺ TỦ — `P-6` mục `A.7` (và đóng `NỢ-P4-01`: `DeviceHealth` chưa từng lên trình duyệt).
 *
 * 🔴 BẤM MỚI HỎI, KHÔNG TỰ HỎI THEO NHỊP. `POST /api/lenh/suckhoe` là một cuộc HỎI–ĐÁP với
 *   tủ thật: máy chủ gửi khung xuống và chờ tới 10 giây. Treo nó vào một `useQuery` có
 *   `refetchInterval` là đẻ ra một dòng lệnh xuống MỌI tủ đang mở trên MỌI màn hình đang mở —
 *   một trang web mở quên trong phòng trực thành một máy phát khung.
 *
 * ⛔ VÀ KHÔNG CÓ SỐ NÀO ĐƯỢC VẼ KHI CHƯA BẤM. Kết quả cũ nằm lại sau khi bấm là ĐÚNG (người ta
 *   cần đọc nó), nhưng nó phải MANG THEO MỐC GIỜ ĐỌC — cùng cái bẫy `soDo` đã trả giá: một kết
 *   luận đúng lúc chấm nằm im trên màn hình rồi được đọc như đang đúng.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
type LyDoKhoiDongTuMayChu = {
  /** Số thô tủ khai. `null` = trường vắng ⇒ tủ KHÔNG khai (luật số 0), không phải 0. */
  so: number | null;
  ma: string;
  /** 🔴 Nhãn do MÁY CHỦ dựng — trình duyệt KHÔNG giữ bản sao thứ hai của bảng mã (L67). */
  nhan: string;
  /** `true` ⇔ SỰ CỐ hoặc mã lạ — hai ca phải kêu lên, không được nuốt. */
  keu: boolean;
  /** Đồng hồ TỦ lúc dựng khung. Không phải lúc tủ khởi động lại — `DeviceHealth` không có mốc ấy. */
  tuKhaiLucMs: number | null;
  /** Tủ có một nguồn giờ nào không. ⚠ "Có nguồn" ≠ "giờ đúng": một RTC trôi ba tiếng vẫn khai `true`. */
  gioTuCoNguon: boolean;
  /** Đồng hồ MÁY CHỦ lúc nhận — mốc duy nhất luôn tin được. */
  mayChuNhanMs: number;
};

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ➕ `GD-03` — SỨC KHOẺ TỦ THÀNH **MỘT DÒNG** trong thẻ "Cần chú ý"; chi tiết vào một hộp.
 *
 * Bản cũ là một thẻ riêng, 11 hàng nhãn-giá-trị, nằm giữa trang — và **trống rỗng cho tới
 * khi ai đó bấm**. Một khối to rỗng giữa trang dạy mắt bỏ qua đúng chỗ ấy.
 *
 * ⛔ HAI LUẬT CŨ GIỮ NGUYÊN TỪNG CHỮ, và cả hai đều dễ mất khi thu nhỏ:
 *   ⓵ **BẤM MỚI HỎI.** `POST /api/lenh/suckhoe` là một cuộc hỏi–đáp với tủ thật (máy chủ chờ
 *      tới 10 giây). Treo nó vào `useQuery` có `refetchInterval` là biến mọi trang mở quên
 *      trong phòng trực thành một máy phát khung.
 *   ⓶ **KẾT QUẢ PHẢI MANG MỐC GIỜ ĐỌC.** Một kết luận đúng lúc chấm nằm im trên màn hình rồi
 *      được đọc như đang đúng — đúng cái bẫy `soDo` đã trả giá.
 * ⚠ Và dòng tóm tắt phân biệt BA ca: chưa đọc (xám) · đọc rồi và có cờ kêu (hổ phách) · đọc
 *   rồi và không cờ nào kêu (lá). "Chưa đọc" KHÔNG được vẽ thành "ổn".
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
function DongSucKhoe({ deviceId, online }: { deviceId: string | null; online: boolean }) {
  const toast = useToast();
  const [moChiTiet, setMoChiTiet] = useState(false);
  const [docLucMs, setDocLucMs] = useState<number | null>(null);
  const doc = useMutation({
    mutationFn: () => api.post<{ echo?: Record<string, any>; lyDo?: LyDoKhoiDongTuMayChu }>(
      '/api/lenh/suckhoe', { deviceId }),
    onSuccess: () => setDocLucMs(Date.now()),
    onError: (e) => toast.error('Không đọc được sức khoẻ tủ', errorMessage(e)),
  });

  const h = doc.data?.echo ?? null;
  const lyDo = doc.data?.lyDo ?? null;

  /*
   * ⛔ ĐẾM CỜ KÊU BẰNG MỘT DANH SÁCH VIẾT RA, không bằng "có trường nào true không": mỗi cờ ở
   *   đây có CHIỀU KÊU RIÊNG — `timeTrusted` kêu khi **thiếu**, `rtcLostPower` kêu khi **có**.
   *   Một phép đếm chung chiều sẽ kêu ngược ở đúng một nửa số cờ.
   */
  const soCoKeu = !doc.data ? null : [
    h?.timeTrusted !== true,            // KHÔNG khai nguồn giờ ⇒ lịch đóng cắt vô nghĩa
    h?.rtcLostPower === true,           // RTC từng mất điện
    h?.modbusOnline !== true,           // không đọc được công tơ
    h?.perOutputOverwrittenByV1 === true,
    !!h?.nvsDefaultedKeys,
    lyDo?.keu === true,
  ].filter(Boolean).length;

  const d: DongCanChuY = !doc.data
    ? { mau: 'chua-ro', cau: 'Sức khoẻ tủ: chưa đọc lần nào',
      phu: online
        ? 'Bấm “Đọc sức khoẻ” để lấy số từ tủ.'
        : 'Tủ đang không nối — đây là câu hỏi gửi thẳng xuống tủ, không hỏi được lúc này.' }
    : soCoKeu === 0
      ? { mau: 'tot', cau: 'Sức khoẻ tủ: không cờ nào kêu',
        phu: `Đọc lúc ${docLucMs ? fmtTime(docLucMs) : '—'}.` }
      : { mau: 'canh', cau: `Sức khoẻ tủ: ${soCoKeu} mục cần xem`,
        phu: `Đọc lúc ${docLucMs ? fmtTime(docLucMs) : '—'} — bấm để xem chi tiết.` };

  return (
    <>
      <DongChuY
        d={d}
        phai={
          <span className="flex shrink-0 items-center gap-1">
            {doc.data && (
              <Button size="sm" variant="ghost" onClick={() => setMoChiTiet(true)}>Chi tiết</Button>
            )}
            <Button
              size="sm" variant="ghost"
              loading={doc.isPending} disabled={!deviceId || !online}
              onClick={() => doc.mutate()}
            >
              Đọc sức khoẻ
            </Button>
          </span>
        }
      />
      <Modal
        open={moChiTiet}
        onClose={() => setMoChiTiet(false)}
        wide
        title="Sức khoẻ tủ"
        subtitle={`Hỏi thẳng tủ một lần mỗi lần bấm — không tự hỏi theo nhịp.${
          docLucMs ? ` Đọc lúc ${fmtDateTime(docLucMs)}.` : ''}`}
      >
      {doc.data && (
        <dl className="space-y-2">
          {/*
            * `Q12a` — nhãn cho MỌI mã 0·1·2·3·4 đến từ một BẢNG ở máy chủ, và mã ngoài bảng
            * hiện ra kèm SỐ HIỆU (`mã 7 — ngoài bảng`). Một mã ta không hiểu là thứ duy nhất
            * bắt buộc phải nói ra con số, vì đó là tất cả những gì còn biết về nó.
            */}
          <Row label="Vì sao tủ khởi động lại">
            {lyDo
              ? (
                <span className="inline-flex flex-wrap items-center justify-end gap-2">
                  <Badge tone={lyDo.keu ? 'red' : 'slate'}>{lyDo.nhan}</Badge>
                  <span className="font-mono text-xs text-chu-3">
                    mã {lyDo.so === null ? '—' : lyDo.so}
                  </span>
                </span>
              )
              : <span className="text-chu-3">máy chủ bản cũ — không trả trường này</span>}
          </Row>
          {/*
            * `Q12b` là một BẢN GHI QUAN SÁT, không phải một mốc sự kiện — nên câu này đọc là
            * *"tủ khai lúc …"*, KHÔNG phải *"khởi động lúc …"*. Và khi tủ không có nguồn giờ
            * thì đồng hồ tủ là 1970 + thời gian chạy: nói thẳng ra thay vì vẽ một mốc sai.
            */}
          {lyDo && (
            <Row label="Tủ khai lúc">
              {lyDo.gioTuCoNguon
                ? <span>{fmtDateTime(lyDo.tuKhaiLucMs)} <span className="text-chu-3">(đồng hồ tủ)</span></span>
                : <span className="text-canh">tủ KHÔNG có nguồn giờ — đồng hồ tủ không đọc được thành mốc</span>}
            </Row>
          )}
          {lyDo && (
            <Row label="Máy chủ nhận lúc">
              <span>{fmtDateTime(lyDo.mayChuNhanMs)} <span className="text-chu-3">({fmtAgo(lyDo.mayChuNhanMs)})</span></span>
            </Row>
          )}
          {/*
            * ⚠ Các cờ dưới đây đọc THẲNG từ khung, và proto3 bỏ giá trị 0 khỏi dây ⇒ một cờ
            *   `false` và một phần sụn đời cũ CHƯA CÓ trường ấy trông y hệt nhau ở đây. Nên câu
            *   chữ nói *"tủ khai"* chứ không nói *"tủ không có"* — vế thứ hai là một kết luận
            *   khung này không đủ sức đỡ.
            */}
          <Row label="Đồng hồ tủ tin được">
            {h?.timeTrusted ? <Badge tone="green">tủ khai CÓ nguồn giờ</Badge>
              : <Badge tone="red">tủ KHÔNG khai — lịch đóng cắt đang vô nghĩa</Badge>}
          </Row>
          <Row label="Đồng hồ RTC từng mất điện">
            {h?.rtcLostPower ? <Badge tone="red">CÓ — giờ tủ có thể sai</Badge>
              : <span className="text-chu-2">tủ không khai cờ này</span>}
          </Row>
          <Row label="Đồng hồ điện">
            {h?.modbusOnline ? <Badge tone="green">đang đọc được</Badge>
              : <Badge tone="amber">tủ không khai — không đọc được số điện</Badge>}
          </Row>
          {/* GD-14 vòng 2 — tên trường máy (`tokenPresent`, …) rời mặt trước; nguyên văn khung
              `echo` nằm trong "Chi tiết kỹ thuật" cuối hộp, chỉ Quản trị hệ thống thấy. */}
          <Row label="Mã xác thực của tủ">
            {/* quyết 12 — câu nhánh `false` do lee chốt 29/09 (vòng 3). */}
            {h?.tokenPresent ? <span className="text-chu-2">Tủ đã có mã xác thực</span>
              : <span className="text-chu-2">Chưa xác nhận được mã xác thực</span>}
          </Row>
          <Row label="Lịch bị gói v1 kéo cả ba ngõ">
            {h?.perOutputOverwrittenByV1 ? <Badge tone="red">CÓ — cấu hình từng ngõ đã bị xoá</Badge>
              : <span className="text-chu-2">tủ không khai</span>}
          </Row>
          {/*
            * `nvsDefaultedKeys` — bitmask. bit1 (`contactors`) nặng nhất: TOÀN BỘ 48×3 timer
            * của khách đã bị thay bằng mặc định ⇒ tủ đang chạy LỊCH MẶC ĐỊNH, không phải lịch
            * người dùng đặt. Hiện số thô kèm câu, không chỉ hiện tên mã.
            */}
          <Row label="Khoá NVS bị trả về mặc định">
            {h?.nvsDefaultedKeys
              ? (
                <span className="text-nguy">
                  0x{Number(h.nvsDefaultedKeys).toString(16)}
                  {(Number(h.nvsDefaultedKeys) & 0b10) ? ' — LỊCH của khách đã bị thay bằng mặc định' : ''}
                </span>
              )
              : <span className="text-chu-2">tủ không khai khoá nào</span>}
          </Row>
          {/*
            * 🔴 `gpsFixSeconds = 0` KHÔNG phải "bắt fix trong 0 giây" — nó là "CHƯA có chu kỳ
            *   nào hoàn tất". Tủ đã kẹp sẵn 1 giây để số 0 chỉ còn đúng một nghĩa; vẽ nó thành
            *   một con số tốt là đúng bệnh 0.000 A của NV5.
            */}
          <Row label="Bắt GPS mất">
            {h?.gpsFixSeconds
              ? <span>{Number(h.gpsFixSeconds)} giây</span>
              : <span className="text-chu-2">CHƯA có chu kỳ nào hoàn tất — không phải “0 giây”</span>}
          </Row>
          <Row label="Ký tự GPS hỏng">
            {h?.gpsBadPermille
              ? (
                <span className={Number(h.gpsBadPermille) > 50 ? 'text-nguy' : ''}>
                  {Number(h.gpsBadPermille)}‰{Number(h.gpsBadPermille) > 50 ? ' — đang rớt ký tự trên đường truyền' : ''}
                </span>
              )
              : <span className="text-chu-2">tủ không khai</span>}
          </Row>
        </dl>
      )}
      {doc.data && (
        <ChiTietKyThuat>
          <pre className="whitespace-pre-wrap break-words font-mono text-xs">{JSON.stringify(doc.data, null, 2)}</pre>
        </ChiTietKyThuat>
      )}
      </Modal>
    </>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-vien pb-2 last:border-0">
      <dt className="shrink-0 text-xs uppercase tracking-wide text-chu-3">{label}</dt>
      <dd className="min-w-0 text-right text-sm text-chu-1">{children}</dd>
    </div>
  );
}

function OutputCard({
  output, nguonGiuSong, online, qd, busy, onToggle,
}: {
  output: CabinetOutput;
  /* Khung sống thắng CSDL: tươi hơn. CSDL hôm nay không có cột này, nên vế phải luôn vắng —
     giữ `??` để ngày có cột thì thứ tự ưu tiên đã đúng sẵn, không phải nhớ sửa. */
  nguonGiuSong: number | undefined;
  online: boolean;
  qd: QuyetDinhCoTuyen | 'khong-co-tuyen' | 'chua-biet';
  busy: boolean;
  onToggle: (on: boolean) => void;
}) {
  /*
   * ⚠ `canControl` vẫn còn — nhưng nay nó được DẪN XUẤT ở đây, không nhận từ ngoài. Cái cổng
   *   không đổi (`'chua-biet'` vẫn không bấm được, hỏng về phía đóng); cái đổi là thẻ này còn
   *   giữ được `qd` để biết mình có gì để KHAI hay không.
   */
  const canControl = qd === 'cho-qua';
  const activeTimers = output.timers.filter((t) => t.enabled).length;
  const maNguon = nguonGiuSong ?? output.nguonGiuQuyen;
  const nguonGiu = docNguonGiu(maNguon);
  const nhanGiu = nhanGiuNgo(maNguon, output.manualHold);
  const chiDuong = cauTraQuyen(nguonGiu);

  return (
    <div className={`rounded-xl border p-3 transition ${output.isOn ? 'border-tot/50 bg-tot/15' : 'border-vien bg-nen-1'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-chu-1">{output.name}</p>
          <p className="text-xs text-chu-2">Ngõ ra {output.index}</p>
        </div>
        <Switch
          checked={output.isOn}
          busy={busy}
          disabled={!canControl || !online}
          onChange={onToggle}
          labelOn="Đang bật"
          labelOff="Đang tắt"
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <Badge tone={output.isOn ? 'green' : 'slate'}>{output.isOn ? 'ĐANG BẬT' : 'ĐANG TẮT'}</Badge>
        <Badge tone="blue">{MODE_LABEL[output.mode]}</Badge>
        {!output.enabled && <Badge tone="amber">Ngõ ra bị khoá</Badge>}
        {/*
          * 🔴 NHÃN NAY TRẢ LỜI "AI GIỮ", KHÔNG CÒN CHỈ "CÓ AI GIỮ" — luật lee 08/09:
          *   *"GIẢI TOẢ: phải là lệnh giải toả TỪ CHÍNH NGUỒN ĐANG GIỮ."* ⇒ không biết AI thì
          *   không biết phải đi đâu. Ba lối ra nằm ở `nhanGiuNgo()`, không nằm ở đây.
          */}
        {nhanGiu.kieu !== 'trong' && (
          <Badge tone="amber"><Hand className="h-3 w-3" />{nhanGiu.nhan}</Badge>
        )}
      </div>

      {/*
        * ⛔ CÂU CHỈ ĐƯỜNG — NÓI, KHÔNG CHẶN. Luật lee: *"Cảnh báo hỗ trợ KHÔNG ĐƯỢC chặn quyền
        *   điều khiển."* Nút gạt phía trên **không** đọc biến này; nó chỉ đọc `canControl` và
        *   `online` như cũ. Câu này cũng KHÔNG tiên đoán *"bấm sẽ bị từ chối"* — app chỉ nói
        *   về số đo nó nhận được (YC-47).
        */}
      {chiDuong && <p className="mt-2 text-xs text-canh">{chiDuong}</p>}

      {/*
        * 🔴 09/10/2026 — lee khoanh đỏ hai dòng ở CẢ BA thẻ ngõ ra (sáu khối trên H3).
        *   "N timer đang bật trong tổng 48" và "Đổi trạng thái lần cuối: 24 giây trước" nhân ba
        *   là sáu dòng chữ nhỏ chiếm nửa khối điều khiển — thứ người trực đang tìm ở đây là
        *   CÔNG TẮC, không phải một bản thống kê.
        * ⚠ Số timer KHÔNG mất: nó về làm `title` của giờ kế tiếp, và trang Lịch đóng cắt vẫn là
        *   chỗ đọc nó cho tử tế.
        * ⚠ "Kế tiếp: bật … tắt …" Ở LẠI — đó là câu duy nhất trả lời *"lát nữa nó làm gì"*.
        */}
      <div className="mt-2 space-y-0.5 text-xs text-chu-2">
        {(output.nextOnMin != null || output.nextOffMin != null) && (
          <p title={`${activeTimers} timer đang bật trong tổng 48`}>
            Kế tiếp: bật {output.nextOnMin != null ? minToStr(output.nextOnMin) : '—'} ·
            tắt {output.nextOffMin != null ? minToStr(output.nextOffMin) : '—'}
          </p>
        )}
      </div>

      {/*
        * 🔴 BA TRẠNG THÁI, BA MÀU — đây đúng chỗ lee nhìn ra chúng đang lẫn nhau:
        *   không có quyền ⇒ HỔ PHÁCH + ổ khoá   ·   mất kết nối ⇒ ĐỎ   ·   đang tải ⇒ xám (Spinner)
        * ⚠ Và huy hiệu ở đây KHÔNG mang tên cờ: băng đầu khối đã nói tên rồi. Nhắc ba lần trên
        *   một khung nhìn là làm mắt thôi đọc nó (L52).
        */}
      {/*
        * ➕ `GD-09b` — BA CA Ở ĐÚNG CHỖ BA MÀU ĐÃ ĐƯỢC KHAI. Khối chú thích ngay trên đã viết
        *   *"không có quyền ⇒ HỔ PHÁCH · mất kết nối ⇒ ĐỎ · đang tải ⇒ xám"*, nhưng mã bên
        *   dưới chỉ có HAI nhánh: `!canControl` gộp cả "đã đo, thiếu quyền" lẫn "chưa hỏi
        *   xong `/me`" vào một huy hiệu hổ phách. Một lời khai viết đúng trong chú thích và
        *   sai trong mã — đúng cái `L-ĐO-CÁI-NỐI` nói: tên có mặt không bằng dây có nối.
        */}
      {qd !== 'cho-qua' && (qd === 'chua-biet'
        ? <DauDangKiem className="mt-2" />
        : <HuyHieuKhoa className="mt-2" />)}
      {canControl && !online && <p className="mt-2 text-xs text-nguy">Tủ mất kết nối — không gửi lệnh được.</p>}
    </div>
  );
}

function CommandBadge({ state }: { state: CommandState }) {
  const tone =
    state === 'ACKED' ? 'green'
      : state === 'SENT' ? 'blue'
        : state === 'QUEUED' || state === 'SUPERSEDED' ? 'slate'   // bị lệnh mới thay ≠ thất bại
          : 'red';
  // ⛔ Trạng thái lạ (máy chủ mới hơn web) KHÔNG in mã trần.
  return <Badge tone={tone as 'green' | 'blue' | 'slate' | 'red'}>{COMMAND_STATE_LABEL[state] ?? 'Trạng thái chưa rõ'}</Badge>;
}

/** Tóm tắt payload lệnh thành câu người đọc hiểu được. */


/*
 * TK-16 B4 — bảng P/U/I/cos φ theo pha từ khung trực tiếp. Mỗi ô TỰ VẮNG khi khung không mang số
 * (proto3 bỏ 0 khỏi dây — không vẽ "0" thay cho "không có"). Lý do gửi:
 *   SU_KIEN = biến động lớn (đánh dấu nổi) · DINH_KY = nhịp 30 s · null = firmware cũ, KHÔNG khai.
 */
function BangSoDoTrucTiep({ sd, soPha, donViCongTo }: {
  sd: { meter: Record<string, number>; at: number; lyDo: 'DINH_KY' | 'SU_KIEN' | null } | null; soPha: number | null;
  /** TK-22 C — đơn vị công tơ GỬI (W/kW); `null` = chưa rõ ⇒ "P tổng" không gắn đơn vị. */
  donViCongTo: 'W' | 'kW' | null;
}) {
  if (!sd) return <p className="text-xs text-chu-3">Chưa nhận số đo nào.</p>;
  const m = sd.meter;
  const pha = [1, 2, 3].filter((k) => soPha == null || k <= soPha);
  const o = (v: number | undefined, chuSo: number) => (Number.isFinite(v) ? (v as number).toFixed(chuSo) : '');
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-chu-3">
        {sd.lyDo === 'SU_KIEN' && <Badge tone="amber">Sự kiện — biến động lớn</Badge>}
        {sd.lyDo === 'DINH_KY' && <Badge tone="slate">Định kỳ</Badge>}
        {sd.lyDo === null && <Badge tone="slate">Firmware cũ — không khai lý do</Badge>}
        <span className="tabular-nums">khung lúc {new Date(sd.at).toLocaleTimeString('vi-VN')}</span>
      </div>
      <table className="w-full text-sm tabular-nums" data-o="so-do-truc-tiep">
        <thead>
          <tr className="text-xs text-chu-3"><th className="text-left">Pha</th><th>U (V)</th><th>I (A)</th><th>P</th><th>cos φ</th></tr>
        </thead>
        <tbody>
          {pha.map((k) => (
            <tr key={k}>
              <td>{soPha === 1 ? '—' : ['A', 'B', 'C'][k - 1]}</td>
              <td className="text-center">{o(m[`v${k}`], 1)}</td>
              <td className="text-center">{o(m[`i${k}`], 2)}</td>
              <td className="text-center">{o(m[`p${k}`], 2)}</td>
              <td className="text-center">{o(m[`a${k}`], 2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {Number.isFinite(m.power) && (
        <div className="mt-1">{(() => {
          const h = hienCongSuat(m.power, donViCongTo);   // TK-22 C: không còn "kW" gắn cứng
          return <Metric giaTri={fmtNum(h.tri, h.chuSo)} donVi={h.donVi ?? undefined} coNho />;
        })()}<span className="ml-1 text-xs text-chu-3">P tổng</span></div>
      )}
    </div>
  );
}
